import { useState, useCallback, useEffect } from 'react';
import { useStatusFilter } from './useStatusFilter';
import { useNatureFilter } from './useNatureFilter';
import { useUsers } from './useUsers';
import { useUserFilter } from './useUserFilters';
import ODataDsnsQueryResults from '../pages/dsn-list/queries/ODataDsnsQueryResults';
import {
  DateRangePreset,
  DateRangePresetDays30,
  DateRangeValue,
  getSelectedDateRange,
} from '../utils/dateHelpers';
import { useDsnFilterState } from './useDsnFilterState';
import { ODataQueryResultsReturn, PaginationState } from '../pages/dsn-list/types/odatadsn.types';
import { DateRangeChangeEvent } from '../types/types';

/**
 * Hook pour gérer l'état de la liste des DSN, y compris les filtres, la pagination et les dates
 */
type InitialPagination = Pick<PaginationState, 'currentPage' | 'pageSize'>;

export const useDsnListState = (initialPagination?: InitialPagination) => {
  // Récupération des données et des fonctions de l'API
  const {
    data,
    pagination,
    error,
    loading: isDataLoading,
    setIsTest: setQueryIsTest,
    setSelectedStatus: setQuerySelectedStatus,
    setSelectedNatures: setQuerySelectedNatures,
    setSelectedUserIds: setQuerySelectedUserIds,
    setPagination: setQueryPagination,
    setSearchTerm: setQuerySearchTerm,
    setDateRangeValue: setQueryDateRangeValue,
    sort,
    handleSort,
  }: ODataQueryResultsReturn = ODataDsnsQueryResults(initialPagination);

  // Hooks liés aux filtres
  const { handleStatusChange: originalHandleStatusChange } = useStatusFilter();
  const { handleNatureChange: originalHandleNatureChange } = useNatureFilter();
  const { users } = useUsers();
  const { handleFilterChange } = useUserFilter(users);

  const {
    selectedUserIds: filterSelectedUserIds,
    setSelectedUserIds: setFilterSelectedUserIds,
    isTest: filterIsTest,
    setIsTest: setFilterIsTest,
    selectedStatus: filterSelectedStatus,
    setSelectedStatus: setFilterSelectedStatus,
    selectedNatures: filterSelectedNatures,
    setSelectedNatures: setFilterSelectedNatures,
    searchTerm: filterSearchValue,
    setSearchTerm: setFilterSearchValue,

    // Date range
    dateRangeValue: filterDateRange,
    setDateRangeValue: setFilterDateRange,

    // Pagination
    pageSize: filterPageSize,
    setPageSize: setFilterPageSize,
    currentPage: filterCurrentPage,
    setCurrentPage: setFilterCurrentPage,
  } = useDsnFilterState();

  const [dsnDatePreset, setDsnDatePreset] = useState<DateRangePreset>(DateRangePresetDays30);
  const [dsnDateStart, setDsnDateStart] = useState<Date | null>(null);
  const [dsnDateEnd, setDsnDateEnd] = useState<Date | null>(null);
  const initialCurrentPage = initialPagination?.currentPage ?? filterCurrentPage;
  const initialPageSize = initialPagination?.pageSize ?? filterPageSize;

  /**
   * Initialisation des filtres généraux au chargement du composant (une seule fois)
   */
  useEffect(() => {
    // Initialiser les paramètres généraux
    setQueryIsTest(filterIsTest);
    setFilterSearchValue(filterSearchValue ?? '');
    setFilterCurrentPage(initialCurrentPage);
    setFilterPageSize(initialPageSize);
    setQueryPagination({
      pageSize: initialPageSize,
      currentPage: initialCurrentPage,
      totalCount: pagination?.totalCount || 0,
    });

    const dateRangeInit = filterDateRange;

    setDsnDatePreset(dateRangeInit.preset);
    setDsnDateStart(dateRangeInit.start ? new Date(dateRangeInit.start) : null);
    setDsnDateEnd(dateRangeInit.end ? new Date(dateRangeInit.end) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUserSelect = useCallback(
    (event: { target: { value: string[] | string } }) => {
      const value = event.target.value;
      if (Array.isArray(value)) {
        setFilterSelectedUserIds(value);
      }
    },
    [setFilterSelectedUserIds],
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      const newStatus = status === filterSelectedStatus ? null : status;
      setFilterSelectedStatus(newStatus);
      setQuerySelectedStatus(newStatus || '');
      originalHandleStatusChange?.(newStatus || '');
      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      filterSelectedStatus,
      setFilterSelectedStatus,
      setQuerySelectedStatus,
      originalHandleStatusChange,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  const updateDsnDateRangeFilter = useCallback(
    (userDateRange: DateRangeValue) => {
      setFilterDateRange(userDateRange);
      setQueryDateRangeValue(userDateRange);
    },
    [setFilterDateRange, setQueryDateRangeValue],
  );

  // Fonction pour gérer le changement du préréglage de date
  const handleDatePresetChange = useCallback(
    (preset: DateRangePreset) => {
      const userDateRange: DateRangeValue = {
        preset: preset,
        start: dsnDateStart?.toISOString() || null,
        end: dsnDateEnd?.toISOString() || null,
      };

      setDsnDatePreset(preset);
      updateDsnDateRangeFilter(userDateRange);
      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      dsnDateStart,
      dsnDateEnd,
      setDsnDatePreset,
      updateDsnDateRangeFilter,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  /**
   * Gère les changements de sélection de dates dans le composant DateRange
   * Cette fonction n'est active que si le mode personnalisé est sélectionné
   */
  const handleDateRangeChange = useCallback(
    (event: DateRangeChangeEvent) => {
      // Extraire les dates de début et de fin depuis l'événement
      const [start, end] = event.target.value;

      const userStartDate = start?.rawValue ? new Date(start.rawValue) : null;
      const userEndDate = end?.rawValue ? new Date(end.rawValue) : null;
      const userDateRange: DateRangeValue = {
        preset: dsnDatePreset,
        start: userStartDate?.toISOString() || null,
        end: userEndDate?.toISOString() || null,
      };

      setDsnDateStart(userStartDate);
      setDsnDateEnd(userEndDate);
      updateDsnDateRangeFilter(userDateRange);
      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      dsnDatePreset,
      setDsnDateStart,
      setDsnDateEnd,
      updateDsnDateRangeFilter,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  const handleModeChange = useCallback(
    (mode: boolean) => {
      setFilterIsTest(mode);
      setQueryIsTest(mode);
      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      setFilterIsTest,
      setQueryIsTest,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  const handleNatureChange = useCallback(
    (natures: string[]) => {
      setFilterSelectedNatures(natures);
    },
    [setFilterSelectedNatures],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setFilterSearchValue(value);
      if (setQuerySearchTerm) {
        setQuerySearchTerm(value);
      }
      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      setQuerySearchTerm,
      setFilterSearchValue,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  const handleSubmitFilters = useCallback(
    (submittedUserIds?: string[] | null, submittedNatures?: string[]) => {
      // Gestion des natures
      const natures = submittedNatures || [];
      setFilterSelectedNatures(natures);
      setQuerySelectedNatures(natures);
      originalHandleNatureChange(natures);

      // Gestion des utilisateurs
      const userIds = submittedUserIds || [];
      setFilterSelectedUserIds(userIds);
      setQuerySelectedUserIds(userIds);

      // Réinitialiser la pagination à la page 1
      setFilterCurrentPage(1);
      setQueryPagination({
        currentPage: 1,
        pageSize: filterPageSize,
        totalCount: pagination?.totalCount || 0,
      });
    },
    [
      setFilterSelectedNatures,
      setQuerySelectedNatures,
      originalHandleNatureChange,
      setFilterSelectedUserIds,
      setQuerySelectedUserIds,
      setFilterCurrentPage,
      setQueryPagination,
      filterPageSize,
      pagination,
    ],
  );

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setFilterPageSize(pageSize);
      setFilterCurrentPage(page);
      setQueryPagination({
        currentPage: page,
        pageSize,
        totalCount: data?.['@odata.count'] || 0,
      });
    },
    [data, setQueryPagination, setFilterPageSize, setFilterCurrentPage],
  );

  const { start: dsnStart, end: dsnEnd } = getSelectedDateRange(filterDateRange);

  return {
    // Données et états
    data,
    error,
    isDataLoading,
    pagination,

    // Filtres courants
    localSelectedNatures: filterSelectedNatures,
    localSelectedStatus: filterSelectedStatus,
    localIsTest: filterIsTest,
    searchValue: filterSearchValue,
    localSelectedUserIds: filterSelectedUserIds || [],

    localStartDate: dsnStart,
    localEndDate: dsnEnd,
    selectedDatePreset: dsnDatePreset,

    // Fonctions de gestion
    handleFilterChange,
    handleUserSelect,
    handleDateRangeChange,
    handleNatureChange,
    handleStatusChange,
    handleModeChange,
    handleSearch,
    handleSubmitFilters,
    handlePaginationChange,
    handleDatePresetChange,
    sort,
    handleSort,
  };
};
